import React from "react";
import { Box, Typography } from "@mui/material";
import { isDogIncluded, dogPossessive } from "@/helpers/dogInclusion";
import { QUIZ } from "@/theme/quizTokens";
import { InclusionSwitch } from "../plan/WhoIsInBoxBlock";

type VariantMeta = {
  title: string;
  image: string;
  grams: number;
  price: number;
};

type Dog = {
  id: string;
  form: any; // replace with your project's DogForm type
};

type UseDogPackBlocksOptions = {
  /** Show the edit-link + include/exclude switch row (only relevant with 2+ dogs). */
  multiDog: boolean;
  /** How many dogs are currently included — used to disable the switch on the last one. */
  includedCount: number;
  /** Called when the person taps "Edit {name}'s meals". */
  onEditMeals: (dogId: string) => void;
  /** Called when the person flips a dog's include/exclude switch. */
  onToggleInclusion: (dogId: string, next: boolean) => void;
};

/** One meal row inside the pack summary card — image, title, grams, qty. */
function PackMealRow({
  image,
  title,
  grams,
  qty,
}: {
  image?: string;
  title: string;
  grams?: number;
  qty: number;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        px: "18px",
        py: "14px",
        borderBottom: "1px solid #e5ddd2",
      }}
    >
      {image && (
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{ width: 48, height: 48, borderRadius: "10px", objectFit: "cover", flexShrink: 0 }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: "15px", color: QUIZ.color.brown, lineHeight: 1.25 }}>
          {title}
        </Typography>
        {grams ? (
          <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "13px", color: "#9098a3", mt: "2px" }}>
            {grams}g
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: "15px", color: QUIZ.color.ink, flexShrink: 0 }}>
        x{qty}
      </Typography>
    </Box>
  );
}

/** Edit-link + include/exclude switch row, shown under a dog's card when there's more than one dog. */
function DogBoxControls({
  dogName,
  dogId,
  disabled,
  onEditMeals,
  onToggleInclusion,
}: {
  dogName: string;
  dogId: string;
  disabled: boolean;
  onEditMeals: (dogId: string) => void;
  onToggleInclusion: (dogId: string, next: boolean) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        px: "18px",
        py: "14px",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onEditMeals(dogId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEditMeals(dogId);
          }
        }}
        sx={{
          fontFamily: QUIZ.font.body,
          fontWeight: 700,
          fontSize: "13.5px",
          color: QUIZ.color.brown,
          textDecoration: "underline",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { opacity: 0.75 },
        }}
      >
        Edit {dogName}'s meals
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontWeight: 700,
            fontSize: "13.5px",
            color: "#f2695c",
          }}
        >
          Remove from box
        </Typography>
        <InclusionSwitch
          included
          disabled={disabled}
          onDark={false}
          label={`${dogName} included in this box`}
          onToggle={(next) => onToggleInclusion(dogId, next)}
        />
      </Box>
    </Box>
  );
}

/** Collapsed row for a dog that's been switched out of the box. */
function ExcludedDogRow({
  dogName,
  dogId,
  poss,
  onToggleInclusion,
}: {
  dogName: string;
  dogId: string;
  poss: string;
  onToggleInclusion: (dogId: string, next: boolean) => void;
}) {
  return (
    <Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    bgcolor: QUIZ.color.white,
    borderRadius: { xs: "18px", md: "14px" },
    px: { xs: "20px", md: "16px" },
    py: { xs: "18px", md: "16px" },
    width: "100%",
    maxWidth: { xs: 340, md: 380 },
    mx: "auto",
  }}
>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 700,
            fontSize: { xs: "24px", md: "21px" },
            color: QUIZ.color.brown,
            lineHeight: 1.2,
          }}
        >
          {dogName}
        </Typography>
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontSize: { xs: "15px", md: "14px" },
            color: QUIZ.color.grey ?? "#6b6b6b",
            lineHeight: 1.25,
            mt: { xs: "4px", md: "2px" },
          }}
        >
          <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
            Not in this box. Click the toggle to add {poss} meals back in.
          </Box>
          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
            Not in this box. Add {poss} meals back with the toggle.
          </Box>
        </Typography>
      </Box>
      <InclusionSwitch
        included={false}
        onDark={false}
        showLabel={false}
        label={`${dogName} included in this box`}
        onToggle={(next) => onToggleInclusion(dogId, next)}
      />
    </Box>
  );
}

/** Pack summary card — dark banner + meal rows + optional FREE benefit rows (+ optional controls row). */
function PackSummaryCard({
  dogName,
  totalMeals,
  totalTubs,
  mealRows,
  freeItems,
  controls,
}: {
  dogName: string;
  totalMeals: number;
  totalTubs: number;
  mealRows: React.ReactNode[];
  /**
   * FREE benefit rows (superfood supplement, delivery, spork, etc). Pass
   * `undefined`/`[]` to omit this whole section — used for multi-dog boxes,
   * where each dog's card only shows the meals + edit/remove controls, not
   * the repeated FREE-items list (that stays single-dog-only).
   */
  freeItems?: { label: string; desc?: string }[];
  controls?: React.ReactNode;
}) {
  return (
<Box
  sx={{
    background: QUIZ.color.white,
    borderRadius: "14px",
    overflow: "hidden",
    width: "100%",
    maxWidth: { xs: 340, md: 380 },
    mx: "auto",
    display: { md: controls ? "flex" : "block" },
    flexDirection: "column",
    height: { md: controls ? "100%" : "auto" },
  }}
>      {/* Dark banner — "IN {NAME}'S BOX · N MEALS IN N TUBS" */}
      <Box sx={{ background: QUIZ.color.brown, px: "18px", py: "12px" }}>
        <Typography
          sx={{
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            fontSize: "16px",
            color: QUIZ.color.white,
            textTransform: "uppercase",
            lineHeight: 1.15,
          }}
        >
          In {dogName}'s box · {totalMeals} meals in {totalTubs} tubs
        </Typography>
      </Box>

      {/* Meal rows */}
      {mealRows}

      {/* FREE rows — omitted entirely when freeItems is not provided (multi-dog boxes) */}
      {freeItems && freeItems.length > 0 && (
        <Box sx={{ px: "18px" }}>
          {freeItems.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: "14px",
                py: "16px",
                borderBottom: i < freeItems.length - 1 ? "1px solid #e5ddd2" : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 800,
                  fontSize: "13px",
                  color: "#f2695c",
                  textTransform: "uppercase",
                  flexShrink: 0,
                  width: "44px",
                  letterSpacing: "0.5px",
                }}
              >
                Free
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: "15px", color: QUIZ.color.ink, lineHeight: 1.35 }}>
                  {item.label}
                </Typography>
                {item.desc && (
                  <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "13px", color: "#9098a3", mt: "2px", lineHeight: 1.4 }}>
                    {item.desc}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Edit-meals + remove-from-box controls (multi-dog only) */}
      {controls && (
        <Box sx={{ borderTop: "1px solid #e5ddd2", mt: { md: "auto" } }}>
          {controls}
        </Box>
      )}
    </Box>
  );
}

/**
 * Builds the array of per-dog summary blocks: banner + meal rows for
 * included dogs (with edit/remove controls when there's more than one dog),
 * and a collapsed "not in this box" row for excluded dogs so they can
 * always be switched back in.
 *
 * FREE benefit rows (superfood supplement, delivery, spork, transition
 * plan, support) are only shown for a single-dog box — with multiple dogs
 * each card stays to meals + controls only, since repeating the same FREE
 * list under every dog's card added noise without new information.
 */
export type DogPackBlock = {
  dogId: string;
  included: boolean;
  content: React.ReactElement;
};

export function useDogPackBlocks(
  dogs: Dog[],
  variantLookup: Record<string, VariantMeta>,
  options: UseDogPackBlocksOptions
): DogPackBlock[] {
  const { multiDog, includedCount, onEditMeals, onToggleInclusion } = options;

  return dogs
    .map((dog) => {
      const dogName = dog.form?.name || "Your dog";
      const included = isDogIncluded(dog.form);

      // Excluded dog collapses to a single muted row with a toggle to add
      // them back — never a dead end.
      if (!included) {
        const poss = dogPossessive(dog.form?.sex);
        return {
          dogId: dog.id,
          included: false,
          content: (
          <Box key={dog.id} sx={{ mb: { xs: "16px", md: includedCount > 1 ? 0 : "16px" } }}>
            <ExcludedDogRow
              dogName={dogName}
              dogId={dog.id}
              poss={poss}
              onToggleInclusion={onToggleInclusion}
            />
          </Box>
          ),
        };
      }

      const mealSel = (dog.form?.mealSelection ?? []) as {
        variantId: string;
        count: number;
      }[];

      const mealRows = mealSel
        .filter((item) => item && item.variantId && item.count > 0)
        .map((item) => {
          const meta = variantLookup[item.variantId];
          if (!meta) return null;
          return (
            <PackMealRow
              key={`meal-${dog.id}-${item.variantId}`}
              image={meta.image}
              title={meta.title}
              grams={meta.grams}
              qty={item.count}
            />
          );
        })
        .filter(Boolean);

      if (!mealRows.length) return null;

      // ---- Banner numbers: derive servings from the saved feeding data ----
      const totalTubs = mealSel.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
      const packSize = Number(dog.form?.selectedPackSize) || 0;
      const dailyGrams = Number(dog.form?.dailyGrams) || Number(dog.form?.dailyKey) || 0;
      const totalMeals =
        packSize > 0 && dailyGrams > 0
          ? Math.max(1, Math.round((totalTubs * packSize) / dailyGrams))
          : totalTubs;

      // ---- FREE benefit rows — single-dog boxes only, see doc comment above ----
      // TODO: replace with your real dynamic business rules
      const freeItems: { label: string; desc?: string }[] | undefined = multiDog
        ? undefined
        : [
            { label: "Superfood supplement", desc: "already in every meal" },
            { label: "Delivery on this box", desc: "Worth £4.99" },
            { label: "Paws spork", desc: "Worth £4.99" },
            {
              label: "Transition plan for fussy pups",
              desc: "Switching too fast is usually what upsets a sensitive tummy. We'll walk you through it.",
            },
            { label: "Our team on hand 7 days a week", desc: "if anything's not right" },
          ];

      return {
        dogId: dog.id,
        included: true,
        content: (
        <Box key={dog.id} sx={{ width: "100%", height: { md: includedCount > 1 ? "100%" : "auto" }, mb: { xs: "32px", md: includedCount > 1 ? 0 : "32px" } }}>
          <PackSummaryCard
            dogName={dogName}
            totalMeals={totalMeals}
            totalTubs={totalTubs}
            mealRows={mealRows}
            freeItems={freeItems}
            controls={
              multiDog ? (
                <DogBoxControls
                  dogName={dogName}
                  dogId={dog.id}
                  disabled={includedCount <= 1}
                  onEditMeals={onEditMeals}
                  onToggleInclusion={onToggleInclusion}
                />
              ) : undefined
            }
          />
        </Box>
        ),
      };
    })
    .filter((block): block is DogPackBlock => block !== null);
}
